import { FC, useState, useCallback, useMemo, useRef, useEffect } from 'react'
import styles from 'components/features/pfm/VideoInPopupPlayer/VideoInPopupPlayer.styl'
import { ImageURLs } from 'interfaces/media'
import { VideoLayout } from 'components/features/pfm/VideoLayout/VideoLayout'
import { OverlayContainer } from 'components/features/pfm/Overlay/OverlayContainer'
import ReactPlayer from 'react-player'
import { IS_MOBILE, IS_STATUS_APP_ANDROID } from 'helpers/userAgentHelpers'
import CloseButton from 'components/atoms/buttons/CloseButton'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { deviceAppStateSelector } from 'selectors/deviceSelector'
import { shouldPlayVideoInPopup } from 'helpers/mobileAppHelpers'

type WideScreenProps = {
  ratio: number
}
const WideScreen: FC<WideScreenProps> = ({ children, ratio }) => (
  <div
    className={ styles.wideScreen }
    style={{
      ['--ratio' as any]: ratio,
    }}
  >
    <div className={ styles.wideScreenHeight }>
      <div className={ styles.wideScreenContent }>
        { children }
      </div>
    </div>
  </div>
)


type VideoInPopupPlayerProps = {
  poster: ImageURLs | null
  videoUrl?: string
  onError?: () => void
}

export const VideoInPopupPlayer: FC<VideoInPopupPlayerProps> = (props) => {
  const { poster, videoUrl, onError } = props

  const ratio = useMemo(() => {
    if (!poster) {
      return 1
    }
    const source = poster.sources[0]
    if (!source) {
      return 1
    }
    return source.width / source.height
  }, [poster])

  const [state, setState] = useState({
    isLoading: false,
    isLoaded: false,
  })
  const { isLoading, isLoaded } = state

  const handlePlay = useCallback(() => {
    setState({
      isLoading: true,
      isLoaded: false,
    })
  }, [])

  const handleClose = useCallback(() => {
    setState({
      isLoading: false,
      isLoaded: false,
    })
  }, [])

  const handleLoaded = useCallback(() => {
    setState({
      isLoading: false,
      isLoaded: true,
    })
  }, [])

  const { appVersion } = useStoreSelector(deviceAppStateSelector)
  const isPopup = useMemo(() => {
    return shouldPlayVideoInPopup(IS_MOBILE, IS_STATUS_APP_ANDROID, appVersion)
  }, [appVersion])

  const closeChild = useMemo(() => {
    if (!isPopup) {
      return null
    }
    return (
      <div className={ styles.closeButton }>
        <CloseButton
          theme='light'
          onClick={ handleClose }
        />
      </div>
    )
  }, [handleClose, isPopup])

  const fullscreenWrapperRef = useRef<HTMLDivElement>(null)
  const [isFullscreenMode, setIsFullscreenMode] = useState(!IS_MOBILE)
  const isAutoplayEnabled = isFullscreenMode && !IS_MOBILE

  const videoChild = useMemo(() => (
    <WideScreen ratio={ ratio }>
      <ReactPlayer
        url={ videoUrl }
        playing={ isAutoplayEnabled }
        width='100%'
        height='100%'
        controls
        config={{
          youtube: {
            playerVars: { origin: `${ location.protocol }//${ location.host }` },
          },
        }}
        onReady={ handleLoaded }
        onError={ onError }
      />
      { closeChild }
    </WideScreen>
  ), [isAutoplayEnabled, handleLoaded, onError, videoUrl, ratio, closeChild])

  const handleFullscreenChange = useCallback(() => {
    const hasFullscreenElement = !!(document as any).fullscreenElement
    setIsFullscreenMode(hasFullscreenElement)
  }, [])

  useEffect(() => {
    const { current } = fullscreenWrapperRef
    if (!current) {
      return
    }

    current.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      current.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [handleFullscreenChange])

  if (!isPopup) {
    return (
      <div
        className={ styles.fullscreenWrapper }
        ref={ fullscreenWrapperRef }
      >
        { isFullscreenMode && (
          <OverlayContainer />
        )}
        { videoChild }
      </div>
    )
  }

  return (
    <>
      <VideoLayout
        poster={ poster }
        isLoading={ isLoading }
        isLoaded={ isLoaded }
        onPlay={ handlePlay }
      />
      { (isLoading || isLoaded) && videoUrl && (
        <OverlayContainer onClose={ handleClose }>
          { videoChild }
        </OverlayContainer>
      )}
    </>

  )
}
